import {
    CustomReportView,
    CustomReportViewDto,
    CustomReportViewFilterGroup,
    CustomReportViewFilterGroupDto,
    JobDetail,
    JobDetailDto,
    Path,
    PathType,
    toCustomReportViewFilterGroupDto,
    toPathType,
    toReportViewFilters,
} from "domain/reports";
import { apiGatewayService, ApiType } from "services/api/ApiGatewayService";

export interface FetchReportViewsResponseDto {
    report_views: CustomReportViewDto[];
}

export interface FetchReportViewsResponse {
    reportViews: CustomReportView[];
}

export interface CreateReportViewResponse {
    reportView: {
        uuid: string;
    };
}

export interface CreatedReportViewWithAdvancedSearchResponse {
    reportView: {
        uuid: string;
    };
}

export interface CreatedReportViewWithAdvancedSearchResponseDto {
    report_view: {
        uuid: string;
    };
}

const REPORT_PATHS_TO_SKIP = [
    "description.description_entries.legacy_report_xml",
    "blancco_issue_report.issues.binary-data",
    "blancco_hardware_report.disks.disk.smart_attributes",
    "blancco_hardware_report.disks.disk.log_pages",
    "blancco_hardware_report.disks.disk.workflow.blobs",
    "blancco_hardware_report.system.workflow.blobs",
    "blancco_erasure_report.erasures.erasure.steps",
    "blancco_erasure_report.erasures.erasure.target.subtarget.files",
];

// Paths whose values are restricted for export (see RestrictedReportPaths.java in bcc-erasure-report-view-service).
// They must stay visible in the report table, XML, column picker and elsewhere, so they're excluded only from the
// export wizard's filename picker, not from REPORT_PATHS_TO_SKIP.
const FILENAME_RESTRICTED_PATHS = [
    "blancco_hardware_report.diagflows",
    "blancco_hardware_report.hardware_functional_tests",
    "blancco_hardware_report.hardware_tests",
    "blancco_hardware_report.image_validation",
    "blancco_hardware_report.processors.test",
    "blancco_hardware_report.processors.processor.test",
    "blancco_hardware_report.processors.processor.test_details.comment",
    "blancco_hardware_report.memory.test",
    "blancco_hardware_report.memory.test_details.comment",
    "blancco_hardware_report.memory.test_details.passes",
    "blancco_hardware_report.display.test",
    "blancco_hardware_report.display.test_details.comment",
    "blancco_hardware_report.motherboard.test",
    "blancco_hardware_report.motherboard.test_details.comment",
    "blancco_hardware_report.keyboard.test",
    "blancco_hardware_report.keyboard.test_details.comment",
    "blancco_hardware_report.pointer.test",
    "blancco_hardware_report.pointer.test_details.comment",
    "blancco_hardware_report.pc_speaker.test",
    "blancco_hardware_report.pc_speaker.test_details.comment",
    "blancco_hardware_report.optical_drives.optical_drive.test",
    "blancco_hardware_report.optical_drives.optical_drive.test_details.comment",
    "blancco_hardware_report.network_interfaces.network_interface.test",
    "blancco_hardware_report.network_interfaces.network_interface.test_details.comment",
    "blancco_hardware_report.usb_ports.test",
    "blancco_hardware_report.usb_ports.test_details.comment",
    "blancco_hardware_report.usb_ports.usb_port.usb_device.average_read_speed",
    "blancco_hardware_report.usb_ports.usb_port.usb_device.average_write_speed",
    "blancco_hardware_report.touchscreens.touchscreen.test",
    "blancco_hardware_report.webcams.webcam.test",
    "blancco_hardware_report.webcams.webcam.test_details.comment",
    "blancco_hardware_report.webcam.test",
    "blancco_hardware_report.webcam.test_details.comment",
    "blancco_hardware_report.batteries.battery.test",
    "blancco_hardware_report.batteries.battery.test_details.comment",
    "blancco_hardware_report.battery_discharge.test",
    "blancco_hardware_report.battery_discharge.test_details.comment",
    "blancco_hardware_report.batteries.battery_discharge.test",
    "blancco_hardware_report.batteries.battery_discharge.test_details.comment",
    "blancco_hardware_report.other_devices.other_device.test",
    "blancco_hardware_report.speakers.test",
    "blancco_hardware_report.sound_cards.test",
    "blancco_hardware_report.cellular_modems.cellular_modem.test",
    "blancco_hardware_report.disks.disk.test",
    "blancco_hardware_report.disks.disk.self_tests.self_test.result",
];

export function isFilenameRestrictedPath(path: string): boolean {
    return FILENAME_RESTRICTED_PATHS.some((restricted) => path === restricted || path.startsWith(`${restricted}.`));
}
const STARTS_WITH_FILTER_OPERATORS = ["VALUE_STARTS_WITH", "WORD_STARTS_WITH"] as const;
const FILTER_OPERATORS = [...STARTS_WITH_FILTER_OPERATORS, "MATCH", "EQUAL", "NOT_EQUAL", "EXIST"] as const;
export type ReportViewFilterOperator = (typeof FILTER_OPERATORS)[number];

export function isStartsWithOperator(operator: ReportViewFilterOperator): boolean {
    return STARTS_WITH_FILTER_OPERATORS.some((each) => each === operator);
}

export function toFilterOperator(value?: string | null): ReportViewFilterOperator {
    if (value == null) {
        return "MATCH";
    }
    // This is possibly a bit paranoid. For any given enum returned from
    // backend, it's always possible that the string isn't a recognized enum
    // property. This way we'll immediately throw an exception when it
    // isn't.
    const found = FILTER_OPERATORS.find((each) => each === value);
    if (found != null) {
        return found;
    }
    throw Error("Unknown FilterOperator: " + value);
}

export interface CommonAdvancedSearchFilter {
    index: number;
    condition: string;
    name: string;
    value: string;
    operator: ReportViewFilterOperator;
    pathType: string;
    innerGroup: AdvancedSearchGroup[] | null;
}

interface AdvancedSearchFilter {
    filters: CommonAdvancedSearchFilter[];
}

export interface AdvancedSearchGroup extends AdvancedSearchFilter {
    groupIndex: number | null;
    groupCondition: string | null;
}

export interface Product {
    id: string;
    name: string;
}

interface CommonCreatedReportViewWithAdvancedSearch {
    name: string;
    shared: boolean;
    columns: string[];
}

export interface CreatedReportViewWithAdvancedSearch extends CommonCreatedReportViewWithAdvancedSearch {
    filters?: CustomReportViewFilterGroup;
}

interface CreatedReportViewWithAdvancedSearchDto extends CommonCreatedReportViewWithAdvancedSearch {
    filters?: CustomReportViewFilterGroupDto;
}

function toCreatedReportViewWithAdvancedSearchDto(
    view: CreatedReportViewWithAdvancedSearch
): CreatedReportViewWithAdvancedSearchDto {
    return {
        name: view.name,
        shared: view.shared,
        columns: view.columns,
        filters: toCustomReportViewFilterGroupDto(view.filters),
    };
}

export interface CreatedReportView {
    name: string;
    shared: boolean;
    columns: string[];
    filters?: CustomReportViewFilterGroup;
}

export interface ImportReportView {
    name: string;
    columns: string[];
    filters?: CustomReportViewFilterGroup;
}

interface CommonReportPath {
    origin: string;
    path: string;
}

interface ReportPathDto extends CommonReportPath {
    product_ids: string[];
    path_type: string;
    add_date: string;
}

export interface ReportPath extends CommonReportPath {
    productIds: string[];
    pathType: string;
    addDate: string;
}

function toCreateReportViewWithAdvancedSearchResponse(responseDto: CreatedReportViewWithAdvancedSearchResponseDto) {
    return {
        reportView: {
            uuid: responseDto.report_view.uuid,
        },
    };
}

function toJob(dto: JobDetailDto): JobDetail {
    return {
        filename: dto.filename,
        status: dto.status,
        timestamp: dto.timestamp,
        tenantUuid: dto.tenant_uuid,
        userUuid: dto.user_uuid,
        totalReportsProcessed: dto.total_reports_processed,
        presignedUrl: dto.presigned_url,
        totalReportsExported: dto.total_reports_exported,
        creationDate: dto.creation_date,
        format: dto.format,
        jobUuid: dto.job_uuid,
        totalReports: dto.total_reports,
        linkExpirationDate: dto.link_expiration_date,
        totalFailures: dto.total_failures,
    };
}

export function toFetchReportViewsResponse(
    responseDto: FetchReportViewsResponseDto,
    paths: ReportPath[]
): FetchReportViewsResponse {
    const pathToType = paths.reduce(
        (map, each) => map.set(each.path, toPathType(each.pathType)),
        new Map<string, PathType>()
    );
    const views: CustomReportView[] = responseDto.report_views.map((each) => ({
        ...each,
        // At least within dev environment users may have persisted views in
        // DynamoDB that begin with "blancco_diagnostic_report.".
        columns: each.columns.map((column) => column.replace(/^blancco_diagnostic_report\./, "")),
        filters: each.filters == null ? undefined : toReportViewFilters(each.filters, pathToType),
    }));
    return {
        reportViews: views,
    };
}

function toReportPaths(paths: ReportPathDto[]): ReportPath[] {
    // Filtered out report paths that mostly contain unnecessary technical information.
    const filteredPaths = paths.filter((item) => !REPORT_PATHS_TO_SKIP.some((pattern) => item.path.includes(pattern)));

    // Trimming might be a little paranoid but frontend should be more robust with it.
    return filteredPaths.map((each) => ({
        origin: each.origin.trim(),
        path: each.path.trim(),
        // Product IDs used to be numeric so here we're preparing for that,
        // making sure that they are strings.
        productIds: (each.product_ids ?? []).map((each) => each.toString()),
        pathType: each.path_type.trim(),
        addDate: each.add_date.trim(),
    }));
}

export function isReportViewDetails(candidate: unknown): candidate is ImportReportView {
    if (typeof candidate !== "object" || candidate == null) {
        return false;
    }
    const record = candidate as Record<string, unknown>;
    for (const property of Object.keys(candidate)) {
        if (record[property] == null) {
            return false;
        }
    }
    return true;
}

/**
 * Drop duplicate paths in reportView while keeping the order.
 */
export function dropDuplicatePaths(reportView: CustomReportView): CustomReportView {
    const seen = new Set<string>();
    const trimmed: string[] = [];
    for (const each of reportView.columns) {
        if (seen.has(each)) {
            continue;
        }
        seen.add(each);
        trimmed.push(each);
    }
    return {
        ...reportView,
        columns: trimmed,
    };
}

const PATH_API_REPORT_PATH = "/api/report-path";
const PATH_API_V1_REPORT_VIEWS = "/api/v1/report-views";

export interface ReportPathFilters {
    [key: string]: string;
}

class ReportViewService {
    private reportPaths: ReportPath[] = [];

    /**
     * Fetch report views.
     *
     * Note! Before this method is called, refreshPaths must have been called first.
     */
    public fetchViews(abortController?: AbortController): Promise<FetchReportViewsResponse> {
        return (
            apiGatewayService
                .invokeApi(PATH_API_V1_REPORT_VIEWS, "GET", null, {
                    abortSignal: abortController?.signal,
                    refreshSession: true,
                    apiType: ApiType.ARTHUR,
                })
                .then((responseDto: FetchReportViewsResponseDto) =>
                    toFetchReportViewsResponse(responseDto, this.reportPaths)
                )
                // This shouldn't be necessary but frontend has at some point
                // allowed creation of views that contain duplicate
                // paths/columns. If we don't remove the duplicates,
                // frontend code will crash later on in Table component.
                .then((response) => ({ reportViews: response.reportViews.map((each) => dropDuplicatePaths(each)) }))
        );
    }

    public fetchExportNotificationDetails(jobId: string, abortController?: AbortController): Promise<JobDetail> {
        return apiGatewayService
            .invokeApi("/api/export-jobs/" + jobId, "GET", null, {
                abortSignal: abortController?.signal,
                apiType: ApiType.LAUREL,
            })
            .then((response: JobDetailDto) => {
                return toJob(response);
            });
    }

    public createView(
        data: CreatedReportViewWithAdvancedSearch,
        abortController: AbortController
    ): Promise<CreatedReportViewWithAdvancedSearchResponse> {
        return apiGatewayService
            .invokeApi<CreatedReportViewWithAdvancedSearchDto, CreatedReportViewWithAdvancedSearchResponseDto>(
                PATH_API_V1_REPORT_VIEWS,
                "POST",
                toCreatedReportViewWithAdvancedSearchDto(data),
                {
                    abortSignal: abortController.signal,
                    refreshSession: true,
                    apiType: ApiType.ARTHUR,
                }
            )
            .then((responseDto: CreatedReportViewWithAdvancedSearchResponseDto) =>
                toCreateReportViewWithAdvancedSearchResponse(responseDto)
            );
    }

    public refreshPaths(abortController?: AbortController, requestParameters?: ReportPathFilters): Promise<void> {
        const keyValuePairs: string[] = [];
        if (typeof requestParameters !== "undefined") {
            Object.keys(requestParameters).forEach((key) => {
                keyValuePairs.push(encodeURI(`${key}=${requestParameters[key]}`));
            });
        }
        let path = PATH_API_REPORT_PATH;
        if (keyValuePairs.length > 0) {
            path = path + "?" + keyValuePairs.join("&");
        }
        return apiGatewayService
            .invokeApi(path, "GET", null, { apiType: ApiType.LAUREL, abortSignal: abortController?.signal })
            .then((dto: { paths: ReportPathDto[] }) => toReportPaths(dto.paths))
            .then((fetchedPaths) => {
                const mapped = [...fetchedPaths].reduce(
                    (accruing, each) => accruing.set(each.path, each),
                    new Map<string, ReportPath>()
                );
                this.reportPaths = Array.from(mapped.values());
                return Promise.resolve();
            });
    }

    public getPaths(): ReportPath[] {
        return [...this.reportPaths, ...this.addAutopilotRecheckPaths()];
    }

    public getFilenamePickerPaths(): ReportPath[] {
        return this.getPaths().filter((each) => !isFilenameRestrictedPath(each.path));
    }

    public deleteView(uuid: string, abortController: AbortController): Promise<void> {
        return apiGatewayService.invokeApi(PATH_API_V1_REPORT_VIEWS + "/" + uuid, "DELETE", null, {
            abortSignal: abortController.signal,
            refreshSession: true,
            apiType: ApiType.ARTHUR,
            emptyResponse: true,
        });
    }

    public editView(
        uuid: string,
        view: CreatedReportViewWithAdvancedSearch,
        abortController: AbortController
    ): Promise<void> {
        const dto = toCreatedReportViewWithAdvancedSearchDto(view);
        return apiGatewayService.invokeApi(PATH_API_V1_REPORT_VIEWS + "/" + uuid, "POST", dto, {
            abortSignal: abortController.signal,
            refreshSession: true,
            apiType: ApiType.ARTHUR,
        });
    }

    private addAutopilotRecheckPaths(): ReportPath[] {
        const commonProperties = {
            origin: "BLANCCO",
            productIds: ["50"],
            pathType: "STRING",
            addDate: "",
        };

        return [Path.AUTOPILOT_RECHECK_STATUS, Path.AUTOPILOT_RECHECK_STATUS_DATE].map((path) => ({
            ...commonProperties,
            path,
        }));
    }
}

export const reportViewService = new ReportViewService();
